
import { AggregatedSessionV2 } from "@global/types/session";
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MultiSegmentSelector,
  SuccessCriteriaSelector,
} from "../../components/segment-selector";
import { useDataStore, useFilters } from "../../context";
import { Segment, SuccessCriterion } from "../../types/analytics";
import { filterSessionsBySegment } from "./analytics";

// GRANULARITY TYPE

type Granularity = "hour" | "day" | "week" | "month";

// SEGMENT COLORS

const SEGMENT_COLORS = [
  "#1976d2", // Blue
  "#dc004e", // Red
  "#388e3c", // Green
  "#f57c00", // Orange
  "#7b1fa2", // Purple
  "#00796b", // Teal
  "#c2185b", // Pink
  "#303f9f", // Indigo
];

// SUCCESS CRITERIA EVALUATION

function evaluateSuccessCriterion(
  session: AggregatedSessionV2,
  criterion: SuccessCriterion
): boolean {
  switch (criterion.type) {
    case "signed_up":
      return session.signedUp;
    case "contacted":
      return session.contacted;
    case "visited_page": {
      if (!criterion.pagePatterns || criterion.pagePatterns.length === 0) {
        return false;
      }
      const pagePaths = (session.pageSessions || []).map((ps) =>
        ps.pagePath.toLowerCase()
      );
      return pagePaths.some((path) =>
        criterion.pagePatterns!.some((pattern) =>
          path.includes(pattern.toLowerCase())
        )
      );
    }
    default:
      return false;
  }
}

// TYPES

interface SegmentMetrics {
  segmentId: string;
  segmentName: string;
  sessions: number;
  successCriteriaRates: Map<string, { count: number; rate: number }>;
  engagedRate: number;
  medianDuration: number;
  medianPages: number;
}

interface TimeSeriesPoint {
  date: string;
  [key: string]: number | string;
}

// HELPER FUNCTIONS

function calculateSegmentMetrics(
  sessions: AggregatedSessionV2[],
  segment: Segment,
  successCriteria: SuccessCriterion[]
): SegmentMetrics {
  const matchingSessions = filterSessionsBySegment(sessions, segment);
  const totalSessions = matchingSessions.length;

  // Calculate success criteria rates
  const successCriteriaRates = new Map<
    string,
    { count: number; rate: number }
  >();
  successCriteria.forEach((criterion) => {
    const matchingCount = matchingSessions.filter((s) =>
      evaluateSuccessCriterion(s, criterion)
    ).length;
    successCriteriaRates.set(criterion.id, {
      count: matchingCount,
      rate: totalSessions > 0 ? matchingCount / totalSessions : 0,
    });
  });

  // Engaged sessions (>1 page AND >10s)
  const engagedSessions = matchingSessions.filter((s) => {
    const pages = new Set((s.pageSessions || []).map((ps) => ps.pagePath)).size;
    const duration = (s.sessionEnd - s.sessionStart) / 1000;
    return pages > 1 && duration > 10;
  }).length;
  const engagedRate = totalSessions > 0 ? engagedSessions / totalSessions : 0;

  // Median duration
  const durations = matchingSessions
    .map((s) => (s.sessionEnd - s.sessionStart) / 1000)
    .sort((a, b) => a - b);
  const medianDuration =
    durations.length > 0 ? durations[Math.floor(durations.length / 2)] : 0;

  // Median pages
  const pageCounts = matchingSessions
    .map((s) => new Set((s.pageSessions || []).map((ps) => ps.pagePath)).size)
    .sort((a, b) => a - b);
  const medianPages =
    pageCounts.length > 0 ? pageCounts[Math.floor(pageCounts.length / 2)] : 0;

  return {
    segmentId: segment.id,
    segmentName: segment.name,
    sessions: totalSessions,
    successCriteriaRates,
    engagedRate,
    medianDuration,
    medianPages,
  };
}

function extractTimeSeriesForSegments(
  sessions: AggregatedSessionV2[],
  segments: Segment[],
  granularity: string,
  metric: "sessions" | "conversions" | "conversionRate"
): TimeSeriesPoint[] {
  if (sessions.length === 0 || segments.length === 0) return [];

  // Get date range
  const sortedSessions = [...sessions].sort(
    (a, b) => a.sessionStart - b.sessionStart
  );
  const startDate = new Date(sortedSessions[0].sessionStart);
  const endDate = new Date(
    sortedSessions[sortedSessions.length - 1].sessionStart
  );

  // Generate all dates in range
  const dates: string[] = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    dates.push(formatDate(current, granularity));
    if (granularity === "hour") current.setHours(current.getHours() + 1);
    else if (granularity === "week") current.setDate(current.getDate() + 7);
    else if (granularity === "month") current.setMonth(current.getMonth() + 1);
    else current.setDate(current.getDate() + 1);
  }

  // Calculate metrics for each segment and date
  return dates.map((date) => {
    const point: TimeSeriesPoint = { date };

    segments.forEach((segment) => {
      const segmentSessions = filterSessionsBySegment(sessions, segment);
      const sessionsOnDate = segmentSessions.filter(
        (s) => formatDate(new Date(s.sessionStart), granularity) === date
      );

      const sessionsCount = sessionsOnDate.length;
      const conversionsCount = sessionsOnDate.filter(
        (s) => s.signedUp || s.contacted
      ).length;

      if (metric === "sessions") {
        point[segment.name] = sessionsCount;
      } else if (metric === "conversions") {
        point[segment.name] = conversionsCount;
      } else {
        point[segment.name] =
          sessionsCount > 0 ? conversionsCount / sessionsCount : 0;
      }
    });

    return point;
  });
}

function extractSuccessCriterionTimeSeries(
  sessions: AggregatedSessionV2[],
  segments: Segment[],
  granularity: Granularity,
  criterion: SuccessCriterion,
  showAbsolute: boolean
): TimeSeriesPoint[] {
  if (sessions.length === 0 || segments.length === 0) return [];

  // Get date range
  const sortedSessions = [...sessions].sort(
    (a, b) => a.sessionStart - b.sessionStart
  );
  const startDate = new Date(sortedSessions[0].sessionStart);
  const endDate = new Date(
    sortedSessions[sortedSessions.length - 1].sessionStart
  );

  // Generate all dates in range
  const dates: string[] = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    dates.push(formatDate(current, granularity));
    if (granularity === "hour") current.setHours(current.getHours() + 1);
    else if (granularity === "week") current.setDate(current.getDate() + 7);
    else if (granularity === "month") current.setMonth(current.getMonth() + 1);
    else current.setDate(current.getDate() + 1);
  }

  // Calculate metrics for each segment and date
  return dates.map((date) => {
    const point: TimeSeriesPoint = { date };

    segments.forEach((segment) => {
      const segmentSessions = filterSessionsBySegment(sessions, segment);
      const sessionsOnDate = segmentSessions.filter(
        (s) => formatDate(new Date(s.sessionStart), granularity) === date
      );

      const totalCount = sessionsOnDate.length;
      const successCount = sessionsOnDate.filter((s) =>
        evaluateSuccessCriterion(s, criterion)
      ).length;

      if (showAbsolute) {
        point[segment.name] = successCount;
      } else {
        point[segment.name] = totalCount > 0 ? successCount / totalCount : 0;
      }
    });

    return point;
  });
}

function extractSessionsTimeSeries(
  sessions: AggregatedSessionV2[],
  segments: Segment[],
  granularity: Granularity,
  _showAbsolute: boolean
): TimeSeriesPoint[] {
  return extractTimeSeriesForSegments(
    sessions,
    segments,
    granularity,
    "sessions"
  );
}

function formatDate(date: Date, granularity: string): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");

  if (granularity === "hour") return `${year}-${month}-${day} ${hour}:00`;
  if (granularity === "month") return `${year}-${month}`;
  return `${year}-${month}-${day}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 0) return "0s";

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  if (days > 0) {
    if (hours > 0) return `${days}d ${hours}h`;
    return `${days}d`;
  }
  if (hours > 0) {
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  if (minutes > 0) {
    if (secs > 0) return `${minutes}m ${secs}s`;
    return `${minutes}m`;
  }
  return `${secs}s`;
}

// SEGMENT COMPARISON TABLE WITH SUCCESS CRITERIA

interface SegmentComparisonTableProps {
  segments: Segment[];
  metricsMap: Map<string, SegmentMetrics>;
  successCriteria: SuccessCriterion[];
  loading?: boolean;
}

const SegmentComparisonTable: React.FC<SegmentComparisonTableProps> = ({
  segments,
  metricsMap,
  successCriteria,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Segment Comparison
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Paper>
    );
  }

  if (segments.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Segment Comparison
        </Typography>
        <Alert severity="info">
          Select one or more segments above to view their performance.
        </Alert>
      </Paper>
    );
  }

  // Single segment view - show boxes instead of table
  if (segments.length === 1) {
    const segment = segments[0];
    const metrics = metricsMap.get(segment.id);

    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {segment.name} Overview
        </Typography>
        <Grid container spacing={2}>
          {/* Sessions Box */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {metrics?.sessions.toLocaleString() || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sessions
              </Typography>
            </Paper>
          </Grid>

          {/* Success Criteria Boxes */}
          {successCriteria.map((criterion) => {
            const criterionData = metrics?.successCriteriaRates.get(
              criterion.id
            );
            const rate = criterionData?.rate || 0;
            const count = criterionData?.count || 0;
            return (
              <Grid key={criterion.id} size={{ xs: 6, sm: 4, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor:
                      rate > 0.1
                        ? "success.50"
                        : rate > 0.05
                        ? "warning.50"
                        : "grey.50",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={
                      rate > 0.1
                        ? "success.main"
                        : rate > 0.05
                        ? "warning.main"
                        : "text.primary"
                    }
                  >
                    {(rate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {criterion.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({count.toLocaleString()})
                  </Typography>
                </Paper>
              </Grid>
            );
          })}

          {/* Engaged Rate Box */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {((metrics?.engagedRate || 0) * 100).toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Engaged Rate
              </Typography>
            </Paper>
          </Grid>

          {/* Median Duration Box */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {formatDuration(metrics?.medianDuration || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Median Duration
              </Typography>
            </Paper>
          </Grid>

          {/* Median Pages Box */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                {(metrics?.medianPages || 0).toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Median Pages
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  const cellStyle = {
    padding: "10px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    backgroundColor: theme.palette.grey[50],
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Segment Comparison
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: "left" }}>Metric</th>
              {segments.map((segment, index) => (
                <th
                  key={segment.id}
                  style={{
                    ...headerCellStyle,
                    textAlign: "right",
                    color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
                  }}
                >
                  {segment.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Sessions Row */}
            <tr>
              <td style={{ ...cellStyle, fontWeight: 500 }}>Sessions</td>
              {segments.map((segment) => {
                const metrics = metricsMap.get(segment.id);
                return (
                  <td
                    key={segment.id}
                    style={{ ...cellStyle, textAlign: "right" }}
                  >
                    {metrics?.sessions.toLocaleString() || 0}
                  </td>
                );
              })}
            </tr>

            {/* Success Criteria Rows */}
            {successCriteria.map((criterion) => (
              <tr key={criterion.id}>
                <td style={{ ...cellStyle }}>
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      {criterion.name}
                    </Typography>
                    {criterion.description && (
                      <Typography variant="caption" color="text.secondary">
                        {criterion.description}
                      </Typography>
                    )}
                  </Stack>
                </td>
                {segments.map((segment) => {
                  const metrics = metricsMap.get(segment.id);
                  const criterionData = metrics?.successCriteriaRates.get(
                    criterion.id
                  );
                  const rate = criterionData?.rate || 0;
                  const count = criterionData?.count || 0;
                  return (
                    <td
                      key={segment.id}
                      style={{ ...cellStyle, textAlign: "right" }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={
                          rate > 0.1
                            ? "success.main"
                            : rate > 0.05
                            ? "warning.main"
                            : "text.primary"
                        }
                      >
                        {(rate * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({count.toLocaleString()})
                      </Typography>
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Engaged Rate Row */}
            <tr>
              <td style={{ ...cellStyle }}>
                <Stack>
                  <Typography variant="body2" fontWeight={500}>
                    Engaged Rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {">"} 1 page and {">"} 10s duration
                  </Typography>
                </Stack>
              </td>
              {segments.map((segment) => {
                const metrics = metricsMap.get(segment.id);
                return (
                  <td
                    key={segment.id}
                    style={{ ...cellStyle, textAlign: "right" }}
                  >
                    <Typography variant="body2">
                      {((metrics?.engagedRate || 0) * 100).toFixed(1)}%
                    </Typography>
                  </td>
                );
              })}
            </tr>

            {/* Median Duration Row */}
            <tr>
              <td style={{ ...cellStyle }}>Median Duration</td>
              {segments.map((segment) => {
                const metrics = metricsMap.get(segment.id);
                return (
                  <td
                    key={segment.id}
                    style={{ ...cellStyle, textAlign: "right" }}
                  >
                    {formatDuration(metrics?.medianDuration || 0)}
                  </td>
                );
              })}
            </tr>

            {/* Median Pages Row */}
            <tr>
              <td style={cellStyle}>Median Pages</td>
              {segments.map((segment) => {
                const metrics = metricsMap.get(segment.id);
                return (
                  <td
                    key={segment.id}
                    style={{
                      ...cellStyle,
                      textAlign: "right",
                      borderBottom: "none",
                    }}
                  >
                    {(metrics?.medianPages || 0).toFixed(1)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

// TREND CHART (receives granularity and display mode from parent)

interface TrendChartProps {
  title: string;
  segments: Segment[];
  loading?: boolean;
  allSessions: AggregatedSessionV2[];
  extractData: (
    sessions: AggregatedSessionV2[],
    segments: Segment[],
    granularity: Granularity,
    showAbsolute: boolean
  ) => TimeSeriesPoint[];
  granularity: Granularity;
  showAbsolute: boolean;
}

const TrendChart: React.FC<TrendChartProps> = ({
  title,
  segments,
  loading,
  allSessions,
  extractData,
  granularity,
  showAbsolute,
}) => {
  const data = useMemo(
    () => extractData(allSessions, segments, granularity, showAbsolute),
    [allSessions, segments, granularity, showAbsolute, extractData]
  );

  if (loading) {
    return (
      <Paper sx={{ p: 2, height: 300 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Skeleton variant="rectangular" height={240} />
      </Paper>
    );
  }

  if (data.length === 0 || segments.length === 0) {
    return (
      <Paper sx={{ p: 2, height: 300 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 240,
          }}
        >
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      </Paper>
    );
  }

  const formatValue = (value: number) => {
    if (!showAbsolute) return `${(value * 100).toFixed(1)}%`;
    return value.toLocaleString();
  };

  return (
    <Paper sx={{ p: 2, height: 300 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => {
              const date = new Date(value);
              if (granularity === "hour") {
                return `${
                  date.getMonth() + 1
                }/${date.getDate()} ${date.getHours()}:00`;
              }
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={
              !showAbsolute ? (v) => `${(v * 100).toFixed(0)}%` : undefined
            }
          />
          <Tooltip
            formatter={(value: number) => formatValue(value)}
            labelFormatter={(label) => {
              const date = new Date(label);
              if (granularity === "hour") {
                return date.toLocaleString();
              }
              return date.toLocaleDateString();
            }}
          />
          <Legend />
          {segments.map((segment, index) => (
            <Line
              key={segment.id}
              type="monotone"
              dataKey={segment.name}
              stroke={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

// MAIN OVERVIEW PAGE

export const OverviewPage: React.FC = () => {
  const { savedSegments, savedSuccessCriteria } = useFilters();
  const {
    sessions,
    sessionsLoading: loading,
    sessionsError: error,
  } = useDataStore();

  // Global chart controls
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [showAbsolute, setShowAbsolute] = useState(false);

  // Local state for segment selection
  const [comparisonSegments, setComparisonSegments] = useState<Segment[]>(
    () => {
      // Default to only "All Traffic" segment
      const allTraffic = savedSegments.find((s) => s.id === "all");
      return allTraffic ? [allTraffic] : savedSegments.slice(0, 1);
    }
  );

  // Local state for success criteria selection
  const [selectedCriteria, setSelectedCriteria] = useState<SuccessCriterion[]>(
    () => {
      // Default to all saved criteria
      return savedSuccessCriteria;
    }
  );

  // Calculate metrics for each selected segment (including success criteria rates)
  const segmentMetricsMap = useMemo(() => {
    const map = new Map<string, SegmentMetrics>();
    comparisonSegments.forEach((segment) => {
      const metrics = calculateSegmentMetrics(
        sessions,
        segment,
        selectedCriteria
      );
      map.set(segment.id, metrics);
    });
    return map;
  }, [sessions, comparisonSegments, selectedCriteria]);

  // Create data extractors for each success criterion (memoized to prevent re-renders)
  const criteriaExtractors = useMemo(() => {
    const extractors: Map<
      string,
      (
        sessions: AggregatedSessionV2[],
        segments: Segment[],
        granularity: Granularity,
        showAbsolute: boolean
      ) => TimeSeriesPoint[]
    > = new Map();

    selectedCriteria.forEach((criterion) => {
      extractors.set(
        criterion.id,
        (
          sessions: AggregatedSessionV2[],
          segments: Segment[],
          granularity: Granularity,
          showAbsolute: boolean
        ) =>
          extractSuccessCriterionTimeSeries(
            sessions,
            segments,
            granularity,
            criterion,
            showAbsolute
          )
      );
    });

    return extractors;
  }, [selectedCriteria]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Segment Selection Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <MultiSegmentSelector
              selectedSegments={comparisonSegments}
              onSegmentsChange={setComparisonSegments}
              label="Compare Segments"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <SuccessCriteriaSelector
              selectedCriteria={selectedCriteria}
              onCriteriaChange={setSelectedCriteria}
              label="Success Criteria"
            />
          </Grid>
          {/* Global Chart Controls */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={granularity}
                  onChange={(e) =>
                    setGranularity(e.target.value as Granularity)
                  }
                  sx={{ fontSize: "0.85rem" }}
                >
                  <MenuItem value="hour">Hourly</MenuItem>
                  <MenuItem value="day">Daily</MenuItem>
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                </Select>
              </FormControl>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <Typography
                  variant="body2"
                  color={!showAbsolute ? "primary" : "text.secondary"}
                  fontWeight={!showAbsolute ? 600 : 400}
                >
                  %
                </Typography>
                <Switch
                  size="small"
                  checked={showAbsolute}
                  onChange={(e) => setShowAbsolute(e.target.checked)}
                />
                <Typography
                  variant="body2"
                  color={showAbsolute ? "primary" : "text.secondary"}
                  fontWeight={showAbsolute ? 600 : 400}
                >
                  #
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Segment Comparison Table with Success Criteria */}
      <Box sx={{ mb: 3 }}>
        <SegmentComparisonTable
          segments={comparisonSegments}
          metricsMap={segmentMetricsMap}
          successCriteria={selectedCriteria}
          loading={loading}
        />
      </Box>

      {/* Sessions Chart */}
      <Box sx={{ mb: 3 }}>
        <TrendChart
          title="Sessions Over Time"
          segments={comparisonSegments}
          loading={loading}
          allSessions={sessions}
          extractData={extractSessionsTimeSeries}
          granularity={granularity}
          showAbsolute={true}
        />
      </Box>

      {/* Success Criteria Charts - One for each criterion */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Success Criteria Trends
      </Typography>
      <Grid container spacing={3}>
        {selectedCriteria.map((criterion) => {
          const extractor = criteriaExtractors.get(criterion.id);
          if (!extractor) return null;

          return (
            <Grid key={criterion.id} size={{ xs: 12, md: 6 }}>
              <TrendChart
                title={criterion.name}
                segments={comparisonSegments}
                loading={loading}
                allSessions={sessions}
                extractData={extractor}
                granularity={granularity}
                showAbsolute={showAbsolute}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
